import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { IconBaseProps, IconType } from 'react-icons';
import { FiCheck, FiChevronDown, FiSearch, FiX } from 'react-icons/fi';


const renderIcon = (Icon: IconType, props?: IconBaseProps) =>
  React.createElement(Icon as unknown as React.FC<IconBaseProps>, props);

interface ExecuteSearchOptions {
  queryOverride?: string;
  categoryOverride?: string;
  clearInput?: boolean;
  automatic?: boolean;
}

const categories = ['All Category', 'Laptop', 'Desktop', 'Components', 'Accessories', 'Printers', 'Projectors', 'CCTV', 'POS System', 'Software', 'Used Items', 'Offers'];

const categoryToSlug: Record<string, string> = {
  Laptop: 'laptop',
  Desktop: 'desktop-pc',
  Components: 'components',
  Accessories: 'accessories',
  Printers: 'printers-scanners',
  Projectors: 'projectors',
  CCTV: 'cctv',
  'POS System': 'pos-items',
  Software: 'software',
  'Used Items': 'used-items',
  Offers: 'offers',
};

const HeaderSearch: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [category, setCategory] = useState(categories[0]);
  const [isFocused, setIsFocused] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingSearch = () => {
    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  };

  useEffect(() => () => cancelPendingSearch(), []);

  useEffect(() => {
    cancelPendingSearch();
    setQuery(searchParams.get('q') || '');
    const rawSlug = searchParams.get('category');
    const slug = rawSlug === 'printers' ? 'printers-scanners' : rawSlug === 'cctv-2' ? 'cctv' : rawSlug === 'pos-system' ? 'pos-items' : rawSlug;
    setCategory(categories.find((option) => categoryToSlug[option] === slug) || categories[0]);
  }, [searchParams]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsCategoryOpen(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);

  const handleResultClick = () => {
    setQuery('');
    setIsFocused(false);
  };

  const handleCategorySelect = (selectedOption: string) => {
    setCategory(selectedOption);
    setIsCategoryOpen(false);
    executeSearch({ categoryOverride: selectedOption, clearInput: false });
    setIsFocused(false);
  };

  const executeSearch = ({ queryOverride, categoryOverride, clearInput = false, automatic = false }: ExecuteSearchOptions = {}) => {
    cancelPendingSearch();
    const trimmedQuery = (queryOverride ?? query).trim();
    const selectedCategory = categoryOverride ?? category;
    const params = new URLSearchParams();
    if (trimmedQuery) params.set('q', trimmedQuery);
    if (selectedCategory !== 'All Category') {
      params.set('category', categoryToSlug[selectedCategory] || selectedCategory.toLowerCase().replace(/\s+/g, '-'));
    }

    const target = `/search${params.toString() ? `?${params.toString()}` : ''}`;
    if (automatic) {
      if (target !== `${location.pathname}${location.search}`) {
        navigate(target, { replace: location.pathname === '/search' });
      }
    } else {
      navigate(target);
    }

    if (clearInput) {
      handleResultClick();
    }
  };


  return (
    <div ref={searchContainerRef} data-search-expanded={isCategoryOpen} className="relative mx-auto w-full min-w-0 max-w-4xl">
      <div
        className={`flex items-center overflow-hidden rounded-2xl border-2 bg-white transition-all duration-300 ${
          isFocused || isCategoryOpen ? 'border-indigo-500 shadow-indigo-500/20 shadow-xl' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <div className="relative flex h-full min-w-0 flex-1 items-center">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400 sm:pl-4">
            {renderIcon(FiSearch, { size: 18, className: 'sm:h-5 sm:w-5' })}
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              const value = e.target.value;
              setQuery(value);
              cancelPendingSearch();
              searchTimeoutRef.current = setTimeout(() => {
                executeSearch({ queryOverride: value, automatic: true });
              }, 500);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') executeSearch();
              if (e.key === 'Escape') setIsFocused(false);
            }}
            aria-label="Search products"
            onFocus={() => {
              setIsFocused(true);
              setIsCategoryOpen(false);
            }}
            onClick={() => {
              setIsFocused(true);
              setIsCategoryOpen(false);
            }}
            placeholder="Search for products, brands..."
            className="block w-full min-w-0 py-3 pl-10 pr-3 text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 sm:py-3.5 sm:pl-12 sm:pr-4"
          />
          {query.length > 0 && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setQuery('');
                executeSearch({ queryOverride: '', automatic: true });
                inputRef.current?.focus();
              }}
              className="mr-1 flex h-11 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500"
            >
              {renderIcon(FiX, { size: 16 })}
            </button>
          )}
        </div>

        <div className="h-8 w-px bg-slate-200 shrink-0" />

        {/* CUSTOM CATEGORY TRIGGER */}
        <div className="relative shrink-0">
          <button
            type="button"
            aria-expanded={isCategoryOpen}
            aria-controls="header-search-categories"
            onKeyDown={(event) => {
              if (event.key === 'Escape') setIsCategoryOpen(false);
            }}
            onClick={() => {
              setIsCategoryOpen(!isCategoryOpen);
              setIsFocused(false);
            }}
            className="flex min-w-[112px] items-center justify-between gap-1.5 px-2.5 py-3 text-xs font-bold text-slate-700 outline-none transition-colors hover:text-indigo-600 sm:min-w-[132px] sm:gap-2 sm:px-4 sm:py-3.5 sm:text-sm"
          >
            <span className="max-w-[82px] truncate whitespace-nowrap sm:max-w-[104px]">{category}</span>
            {renderIcon(FiChevronDown, { size: 16, className: `transition-transform duration-300 ${isCategoryOpen ? 'rotate-180' : ''}` })}
          </button>
        </div>

        <button
          type="button"
          aria-label="Search"
          onClick={() => executeSearch()}
          className="flex h-11 w-11 shrink-0 items-center justify-center self-stretch bg-gradient-to-r from-indigo-600 to-indigo-500 text-white sm:h-12 sm:w-16"
        >
          {renderIcon(FiSearch, { size: 22 })}
        </button>
      </div>
      {isCategoryOpen && (
        <div id="header-search-categories" onKeyDown={(event) => { if (event.key === 'Escape') setIsCategoryOpen(false); }} className="absolute left-0 top-[calc(100%+0.75rem)] z-[100] max-h-80 w-[min(420px,calc(100vw-2rem))] overflow-y-auto rounded-2xl border border-slate-700/50 bg-slate-900 p-2 shadow-2xl animate-slideDown">
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {categories.map((option) => (
              <button
                key={`cat-${option}`}
                type="button"
                onClick={() => handleCategorySelect(option)}
                className={`flex items-center justify-between rounded-xl px-4 py-3 text-left text-sm transition-all ${
                  category === option
                    ? 'bg-indigo-600 text-white font-bold'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <span className="whitespace-nowrap">{option}</span>
                {category === option && renderIcon(FiCheck, { size: 16 })}
              </button>
            ))}
          </div>
        </div>
      )}
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slideDown { animation: slideDown 0.2s ease-out; }
      `}</style>
    </div>
  );
};

export default HeaderSearch;
